import { CircularProgress } from '@mui/material';
import moment from 'moment-timezone';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';

import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { ReduxState } from 'ducks';
import { fetchProductInstances } from 'ducks/client/productInstances';
import { areSameMonth, getWeeksForMonth, isCurrentMonth } from 'lib/util/datePickerHelpers';
import { getLatestFeatureRecurrenceStartDateLocal } from 'lib/util/getLatestFeatureRecurrenceStartDateLocal';
import { getMonthYearFormat } from 'lib/util/i18nHelpers';
import { nonNull } from 'lib/util/nonNull';
import { useAttrByDateMap } from 'hooks/useAttrByDateMap';

import clsx from 'clsx';
import { BookingWidgetThemeContext } from 'contexts/BookingWidgetThemeContext';
import { getThemeCssOverride } from 'components/Themes';
import { getAdjustedPrimaryUnit, getPrimaryUnit } from './util';
import { getAmountFromMoneyString } from 'lib/util/util';

interface Props {
  selectedDate?: string;
  onDateChange: (d: string) => void;
  hideCalendar?: boolean; // Hide calendar but fetch product instances
  shouldShowPricingOnCalendar?: boolean;
}

const calculateDisplayPriceForDay = (
  productInstances: any[],
  day: string,
  timezone: string
): { displayPrice: string; deltaType: 'CHARGE' | 'DISCOUNT' } => {
  // Find all product instances for the given day
  const dayInstances = productInstances.filter(
    (instance) => moment(instance.start_date_time_utc).tz(timezone).format('YYYY-MM-DD') === day
  );

  if (dayInstances.length === 0) {
    return { displayPrice: '0', deltaType: 'DISCOUNT' };
  }

  const arrayOfGrossPrices: number[] = [];
  let dynamicPricingDeltaType: 'CHARGE' | 'DISCOUNT' = 'DISCOUNT';

  // Collect all prices from all instances of the day
  for (const productInstance of dayInstances) {
    // Get normal pricing as default
    if (productInstance?.units && productInstance.units.length > 0) {
      const primaryUnit = getPrimaryUnit(productInstance.units);
      arrayOfGrossPrices.push(getAmountFromMoneyString(primaryUnit?.gross ?? ''));
    }

    // If product instance is subject to dynamic pricing, get adjusted pricing
    if (
      productInstance?.dynamic_pricing_adjusted_unit_prices &&
      productInstance.dynamic_pricing_adjusted_unit_prices.length > 0
    ) {
      for (const adjustedUnitPrice of productInstance.dynamic_pricing_adjusted_unit_prices) {
        const adjustedPrimaryUnit = getAdjustedPrimaryUnit(adjustedUnitPrice.prices);
        dynamicPricingDeltaType = adjustedPrimaryUnit?.delta_type ?? 'DISCOUNT';
        arrayOfGrossPrices.push(
          getAmountFromMoneyString(adjustedPrimaryUnit?.adjusted_gross ?? '')
        );
      }
    }
  }

  // Determine the gross price to show based on dynamic pricing delta type
  if (arrayOfGrossPrices.length > 0) {
    let targetPrice: number;
    if (dynamicPricingDeltaType === 'CHARGE') {
      targetPrice = Math.max(...arrayOfGrossPrices);
    } else {
      // Assume 'DISCOUNT' or any other type defaults to smallest
      targetPrice = Math.min(...arrayOfGrossPrices);
    }

    const displayPrice =
      targetPrice >= 100000 ? (targetPrice / 1000).toFixed(0) + 'k' : targetPrice.toLocaleString();

    return { displayPrice, deltaType: dynamicPricingDeltaType };
  }

  return { displayPrice: '0', deltaType: dynamicPricingDeltaType };
};

export const DatePicker = ({
  onDateChange,
  selectedDate,
  hideCalendar,
  shouldShowPricingOnCalendar,
}: Props) => {
  // Currently visible calendar month as 'YYYY-MM-DD' string
  const [month, setMonth] = React.useState<string>(selectedDate || moment().format('YYYY-MM-DD'));

  const dispatch = useDispatch();

  const product = nonNull(useSelector((state: ReduxState) => state.server.product.product));
  const productInstances = useSelector((state: ReduxState) => state.productInstances.all);
  const loading = useSelector((state: ReduxState) => state.productInstances.loading);
  const groupBookingTemplate = useSelector(
    (state: ReduxState) => state.server.groupBookingTemplate.groupBookingTemplate
  );

  React.useEffect(() => {
    const today = moment().format('YYYY-MM-DD');
    const latestFeatureStartDate = getLatestFeatureRecurrenceStartDateLocal(product);
    setMonth(
      moment(latestFeatureStartDate || today)
        .startOf('month')
        .format('YYYY-MM-DD')
    );
  }, [product]);

  const router = useRouter();
  const isUpgrade = Boolean(router.query.u);

  const { i18n, t } = useTranslation();
  const { apiKey } = React.useContext(ApiKeyContext);

  const locale = i18n ? i18n.language : '';

  React.useEffect(() => {
    let firstDay = moment(month).date(1);
    let lastDay = moment(month).endOf('month').add(1, 'day');

    if (groupBookingTemplate) {
      if (groupBookingTemplate.start_date_local_from > firstDay.format('YYYY-MM-DD')) {
        firstDay = moment(groupBookingTemplate.start_date_local_from);
      }
      if (groupBookingTemplate.start_date_local_to < lastDay.format('YYYY-MM-DD')) {
        lastDay = moment(groupBookingTemplate.start_date_local_to).add(1, 'day');
      }
    }

    if (firstDay && lastDay && firstDay < lastDay) {
      dispatch(
        fetchProductInstances(
          product?.booking_widget_api_key || apiKey,
          product.id,
          firstDay.format('YYYY-MM-DD'),
          lastDay.format('YYYY-MM-DD'),
          i18n.language
        )
      );
    }
  }, [apiKey, i18n.language, month, product.id, groupBookingTemplate, dispatch]);

  const handleNextMonthClick = () => {
    setMonth(moment(month).endOf('month').add(1, 'day').format('YYYY-MM-DD'));
  };
  const handlePrevMonthClick = () => {
    setMonth(moment(month).startOf('month').subtract(1, 'day').format('YYYY-MM-DD'));
  };

  const attrByDateMap = useAttrByDateMap();

  const weeks = getWeeksForMonth(month);

  let showBookingAllowedMessage = false;
  const agentGuestBookingAllowedDaysBefore =
    product?.agent_guest_booking_period_settings?.booking_allowed_days_before_participation;
  const agentGuestBookingAllowedTimesBefore =
    product?.agent_guest_booking_period_settings?.booking_allowed_days_before_participation_time;
  let bookingAllowedMessage = t(
    'You can apply from {{hhmm}}, {{day}} days before the participation date.',
    {
      hhmm: agentGuestBookingAllowedTimesBefore ?? '00:00',
      day: agentGuestBookingAllowedDaysBefore ?? '0',
    }
  );
  if (agentGuestBookingAllowedDaysBefore || agentGuestBookingAllowedTimesBefore) {
    let bookingRestrictedDate = moment().tz(product?.start_timezone ?? '');
    if (agentGuestBookingAllowedDaysBefore) {
      bookingRestrictedDate = bookingRestrictedDate.add(
        agentGuestBookingAllowedDaysBefore + 1,
        'days'
      );
    }
    if (agentGuestBookingAllowedTimesBefore) {
      const [hours, minutes] = agentGuestBookingAllowedTimesBefore.split(':').map(Number);
      bookingRestrictedDate = bookingRestrictedDate.add(hours, 'hours').add(minutes, 'minutes');
    }
    const bookingRestrictedDateFormat = bookingRestrictedDate.format('YYYY-MM-DD');

    const lastDayOfSelectedMonth = moment(month).endOf('month').format('YYYY-MM-DD');

    if (bookingRestrictedDateFormat <= lastDayOfSelectedMonth) {
      showBookingAllowedMessage = true;
      if (!agentGuestBookingAllowedDaysBefore) {
        bookingAllowedMessage = t('You can apply from {{hhmm}} on the day of participation.', {
          hhmm: agentGuestBookingAllowedTimesBefore ?? '00:00',
        });
      } else if (agentGuestBookingAllowedDaysBefore == 1) {
        bookingAllowedMessage = t(
          'You can apply from {{hhmm}}, 1 day before the participation date.',
          {
            hhmm: agentGuestBookingAllowedTimesBefore ?? '00:00',
          }
        );
      }
    }
  }

  const bookingWidgetTheme = React.useContext(BookingWidgetThemeContext);

  if (hideCalendar) {
    return null;
  }

  return (
    <div className="widget-calendar__month">
      <div className="widget-calendar__month__nav">
        <button
          type="button"
          className="widget-calendar__month__nav__prev"
          disabled={isCurrentMonth(month) || isUpgrade}
          onClick={handlePrevMonthClick}
        />
        <p className="widget-calendar__month__nav__title">
          {moment(month).locale(locale).format(getMonthYearFormat(locale))}
        </p>
        <button
          type="button"
          className="widget-calendar__month__nav__next"
          disabled={isUpgrade}
          onClick={handleNextMonthClick}
        />
      </div>
      <div className="widget-calendar__month__title">
        <p>{t('Sun')}</p>
        <p>{t('Mon')}</p>
        <p>{t('Tue')}</p>
        <p>{t('Wed')}</p>
        <p>{t('Thu')}</p>
        <p>{t('Fri')}</p>
        <p>{t('Sat')}</p>
      </div>
      {loading && (
        <CircularProgress
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginTop: '-20px',
            marginLeft: '-20px',
          }}
        />
      )}
      {weeks.map((week, idx) => (
        <div
          className={clsx(
            'widget-calendar__month__week',
            shouldShowPricingOnCalendar ? 'price' : undefined
          )}
          key={idx}
        >
          {week.days.map((day) => {
            if (!areSameMonth(month, day)) {
              return <label key={day} className="blank" />;
            }

            let status = '';
            let hasPromo = false;
            let isLimitedAvailability = false;
            const attr = attrByDateMap[day];
            if (attr) {
              status = attr.status;
              hasPromo = attr.hasPromo;
              isLimitedAvailability = attr.isLimitedAvailability;
            }

            let displayedGrossPrice = '0';

            const isPastDate = (date: string): boolean => {
              if (!date) {
                return false;
              }

              return date < moment().tz(product.start_timezone).format('YYYY-MM-DD');
            };

            // If shouldShowPricingOnCalendar = true, means product has auto price adjustment settings
            if (shouldShowPricingOnCalendar && productInstances && productInstances.length > 0) {
              const { displayPrice } = calculateDisplayPriceForDay(
                productInstances,
                day,
                product.start_timezone
              );
              displayedGrossPrice = displayPrice;
            }

            if (!status || (isUpgrade && day !== selectedDate)) {
              return (
                <label key={day} className="disabled">
                  <p>{moment(day).format('D')}</p>
                </label>
              );
            }

            return (
              <label
                key={day}
                className={clsx(status, getThemeCssOverride(bookingWidgetTheme, status))}
              >
                <input
                  type="radio"
                  name="day"
                  checked={day === selectedDate}
                  onChange={() => {
                    onDateChange(day);
                  }}
                />
                <p>{moment(day).format('D')}</p>
                {/* Use status to check if the date is current date but there is no available time */}
                {/* Do not show pricing if instance is closed */}
                {shouldShowPricingOnCalendar &&
                  !isPastDate(day) &&
                  status &&
                  status !== 'closed' && (
                    <span
                      className={clsx(
                        getThemeCssOverride(
                          bookingWidgetTheme,
                          'widget-calendar__month__week__priceSpan'
                        )
                      )}
                    >
                      {displayedGrossPrice}
                    </span>
                  )}
                {hasPromo && status !== 'closed' && <div className="promotion" />}
                {isLimitedAvailability && status !== 'closed' && (
                  <img className="few" src="/static/images/ic_few.svg" alt="limited availability" />
                )}
              </label>
            );
          })}
        </div>
      ))}
      {showBookingAllowedMessage && (
        <div className="widget-calendar__booking__allowed__text">{bookingAllowedMessage}</div>
      )}
      {
        /* pad with two extra rows if there are only 4 weeks covered by the month's dates */
        weeks.length === 4 && (
          <>
            <div className="widget-calendar__month__week" key={5} />
            <div className="widget-calendar__month__week" key={6} />
          </>
        )
      }
      {
        /* pad with one extra row if there are only 5 weeks covered by the month's dates */
        weeks.length === 5 && <div className="widget-calendar__month__week" key={6} />
      }
    </div>
  );
};
